import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

export interface Profile {
  id: string;
  email: string;
  role: "contractor" | "client";
  name: string | null;
  business_name: string | null;
  phone: string | null;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    role: "contractor" | "client",
    fullName: string,
  ) => Promise<{ error: AuthError | null }>;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error: AuthError | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>;
  sendPasswordResetEmail: (
    email: string,
  ) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  updateProfile: (
    updates: Partial<Profile>,
  ) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function mapAuthError(error: AuthError): AuthError {
  const msg = error.message?.toLowerCase() ?? "";
  const friendly = (text: string) => { error.message = text; return error; };
  if (msg.includes("user already registered") || msg.includes("already been registered"))
    return friendly("An account with this email already exists. Please sign in instead.");
  if (msg.includes("invalid login credentials") || msg.includes("invalid email or password"))
    return friendly("Incorrect email or password. Please try again.");
  if (msg.includes("email not confirmed"))
    return friendly("Please check your email and confirm your account before signing in.");
  if (msg.includes("password should be at least"))
    return friendly("Password must be at least 6 characters.");
  if (msg.includes("unable to validate email address"))
    return friendly("Please enter a valid email address.");
  if (msg.includes("row-level security") || msg.includes("rls"))
    return friendly("An account with this email already exists. Please sign in instead.");
  return error;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const upsertUserRecord = useCallback(async (authUser: User) => {
    const metadata = authUser.user_metadata ?? {};
    const name =
      metadata.full_name ||
      metadata.name ||
      authUser.email?.split("@")[0] ||
      "User";
    const role: "contractor" | "client" =
      metadata.role === "contractor" ? "contractor" : "client";

    const { error } = await supabase.from("users").upsert({
      id: authUser.id,
      email: authUser.email ?? "",
      name,
      role,
      business_name: metadata.business_name ?? null,
      phone: metadata.phone ?? null,
    });

    if (error) throw error;
  }, []);

  const fetchProfile = useCallback(
    async (userId: string, authUser?: User) => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", userId)
          .single();

        if (error) {
          if (authUser) {
            await upsertUserRecord(authUser);
            const { data: refreshed, error: refreshError } = await supabase
              .from("users")
              .select("*")
              .eq("id", userId)
              .single();

            if (refreshError) throw refreshError;
            setProfile(refreshed);
            return;
          }
          throw error;
        }

        setProfile(data);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    },
    [upsertUserRecord],
  );

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id, session.user);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id, session.user);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signUp = async (
    email: string,
    password: string,
    role: "contractor" | "client",
    fullName: string,
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role,
            full_name: fullName,
            name: fullName,
          },
        },
      });

      if (error) {
        return { error: mapAuthError(error) };
      }

      // Supabase returns a user with identities=[] when email already exists in auth
      // (it doesn't reveal this to prevent enumeration, but we can detect it)
      if (data.user && data.user.identities?.length === 0) {
        return {
          error: Object.assign(new Error("An account with this email already exists. Please sign in instead."), { status: 400 }) as unknown as AuthError,
        };
      }

      if (data.user) {
        try {
          await upsertUserRecord(data.user);
        } catch (upsertErr) {
          console.error("Failed to create user profile:", upsertErr);
          // Don't block signup — profile will be created on next login
        }
      }

      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) return { error: mapAuthError(error) };
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) return { error };
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) return { error };
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) return { error };
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error("No user logged in") };

    try {
      const { error } = await supabase
        .from("users")
        .update(updates)
        .eq("id", user.id);

      if (error) throw error;

      // Refresh profile
      await fetchProfile(user.id);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signInWithMagicLink,
    sendPasswordResetEmail,
    updatePassword,
    signOut,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
